import Dexie, { type Table } from 'dexie';
import { registerCleanup } from '../platform/cleanup';
import type { OutboxEntry, MetaRow, RejectionRow } from '../platform/sync-runner';

/**
 * The Schaak module's local database — its own IndexedDB database
 * (`familyhub-schaak`), the module-independence boundary on the client. Rows
 * are kept as the server sends them, tombstones included and filtered at read.
 */

interface Owned {
  id: string;
  created_by: string | null;
  created_at?: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CardRecord extends Owned {
  kind: 'puzzle' | 'mistake' | 'drill' | 'opening';
  ref: string;
  theme: string | null;
  box: number;
  due_at: string | null;
  attempts: number;
  solved: number;
  failed: number;
  first_result: 'solved' | 'failed' | null;
  last_at: string | null;
  reason?: 'threat' | 'hanging' | 'missed' | 'calc' | 'rushed' | 'unsure' | null;
}

export interface ProfileRecord extends Owned {
  rating: number;
  rating_games: number;
  days?: { d: string; r: number; n: number; s: number }[];
}

export interface WoodpeckerRecord extends Owned {
  set_no: number;
  cycle: number;
  started_at: string;
  finished_at: string | null;
  position: number;
  correct: number;
  seconds: number;
}

export interface AccountsRecord extends Owned {
  lichess_username: string | null;
  chesscom_username: string | null;
}

/** Server-owned, pulled read-only (docs/SCHAAK.md §3.2). */
export interface GameRecord {
  id: string;
  user_id: string;
  source: 'lichess' | 'chesscom';
  url: string;
  played_at: string;
  color: 'w' | 'b';
  opponent: string;
  opponent_rating: number | null;
  my_rating: number | null;
  result: 'win' | 'loss' | 'draw';
  speed: string;
  opening: string | null;
  analysed_at: string | null;
  deleted_at: string | null;
}

/** Server-owned, pulled read-only: a position from one of his games. */
export interface MistakeRecord {
  id: string;
  user_id: string;
  game_id: string;
  ply: number;
  fen: string;
  opp_move: string;
  played: string;
  played_san: string;
  best: string;
  best_san: string;
  accept: string[];
  loss: number;
  kind: string;
  /** Stockfish's score from his side (migration 045; absent on older rows). */
  eval_before_cp?: number | null;
  eval_before_mate?: number | null;
  eval_after_cp?: number | null;
  eval_after_mate?: number | null;
  better?: { move: string; san: string; cp: number | null; mate: number | null; pv?: string[] }[] | null;
  /** The engine's best line after his move (046). */
  refutation?: string[] | null;
  created_at: string;
  deleted_at: string | null;
}

export class SchaakDb extends Dexie {
  cards!: Table<CardRecord, string>;
  profile!: Table<ProfileRecord, string>;
  woodpecker!: Table<WoodpeckerRecord, string>;
  accounts!: Table<AccountsRecord, string>;
  games!: Table<GameRecord, string>;
  mistakes!: Table<MistakeRecord, string>;
  outbox!: Table<OutboxEntry, number>;
  rejections!: Table<RejectionRow, string>;
  meta!: Table<MetaRow, string>;

  constructor() {
    super('familyhub-schaak');
    this.version(1).stores({
      cards: 'id, created_by, due_at, deleted_at',
      profile: 'id, created_by',
      woodpecker: 'id, created_by',
      outbox: '++seq, queued_at',
      rejections: 'id, at',
      meta: 'key',
    });
    // Phase 2: his own games (migration 044).
    this.version(2).stores({
      accounts: 'id, created_by',
      games: 'id, user_id',
      mistakes: 'id, user_id, game_id',
    });
  }
}

export const sdb = new SchaakDb();

const asTable = (t: Table<never, string>) => t as unknown as Table<Record<string, unknown>, string>;

export const SCHAAK_TABLE_MAP = [
  { name: 'schaak_cards', table: asTable(sdb.cards as never) },
  { name: 'schaak_profile', table: asTable(sdb.profile as never) },
  { name: 'schaak_woodpecker', table: asTable(sdb.woodpecker as never) },
  { name: 'schaak_accounts', table: asTable(sdb.accounts as never) },
  { name: 'schaak_games', table: asTable(sdb.games as never) },
  { name: 'schaak_mistakes', table: asTable(sdb.mistakes as never) },
];

export async function clearSchaakData(): Promise<void> {
  await Promise.all(
    [sdb.cards, sdb.profile, sdb.woodpecker, sdb.accounts, sdb.games, sdb.mistakes, sdb.outbox, sdb.rejections, sdb.meta].map((t) => t.clear()),
  );
}

registerCleanup(clearSchaakData);
